import { Check, Folder as FolderIcon, Layers } from "lucide-react";
import { useState } from "react";
import { Modal } from "../../components/ui/modal";
import { useToast } from "../../components/ui/toast";
import { cn } from "../../lib/utils";
import { useFolderCounts, useFolders, useMoveAgent } from "./queries";
import type { Agent } from "./types";

export function MoveAgentModal({ agent, onClose }: { agent: Agent; onClose: () => void }) {
  const { data: folders = [] } = useFolders();
  const count = useFolderCounts();
  const move = useMoveAgent();
  const toast = useToast();
  const [selected, setSelected] = useState<string | null>(agent.folder_id);

  const agentName = agent.agent_name || agent.name || "Untitled Agent";
  const unchanged = selected === agent.folder_id;

  const onMove = () => {
    move.mutate(
      { agentId: agent.agent_id, folderId: selected },
      {
        onSuccess: () => { toast.success("Agent moved", agentName); onClose(); },
        onError: (e) => toast.danger((e as Error).message),
      },
    );
  };

  return (
    <Modal
      title="Move Agent"
      subtitle="Select the folder you want to move this agent to"
      onClose={onClose}
      width="max-w-sm"
      footer={
        <>
          <button onClick={onClose} className="rounded-lg border border-line px-4 py-2 text-sm font-semibold text-ink hover:bg-soft">
            Cancel
          </button>
          <button
            onClick={onMove}
            disabled={unchanged || move.isPending}
            className="rounded-lg bg-ink px-4 py-2 text-sm font-semibold text-canvas hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-40"
          >
            {move.isPending ? "Moving…" : "Move Agent"}
          </button>
        </>
      }
    >
      <div className="flex flex-col gap-1">
        <FolderRow
          icon={<Layers className="h-4 w-4" />}
          label="All Agents (No folder)"
          count={count(null)}
          active={selected === null}
          onClick={() => setSelected(null)}
        />
        {folders.map((f) => (
          <FolderRow
            key={f.folder_id}
            icon={<FolderIcon className="h-4 w-4" />}
            label={f.name}
            count={count(f.folder_id)}
            active={selected === f.folder_id}
            onClick={() => setSelected(f.folder_id)}
          />
        ))}
      </div>
    </Modal>
  );
}

function FolderRow({ icon, label, count, active, onClick }: {
  icon: React.ReactNode; label: string; count: number; active: boolean; onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className={cn(
        "flex items-center gap-2.5 rounded-lg px-3 py-2.5 text-left text-sm transition-colors",
        active ? "bg-brand-soft font-semibold text-brand" : "text-ink-2 hover:bg-soft",
      )}
    >
      <span className={active ? "text-brand" : "text-ink-3"}>{icon}</span>
      <span className="min-w-0 flex-1 truncate">{label}</span>
      <span className="font-mono text-[10px] text-ink-3">{count} agent{count === 1 ? "" : "s"}</span>
      {active && <Check className="h-4 w-4 shrink-0 text-brand" />}
    </button>
  );
}
